/**
 * Done. / ChiroCPD — useActivities Hook
 *
 * Fetches /api/activities with optional status filter.
 * Provides CRUD operations that invalidate the export cache on mutation.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '@/lib/api';
import { cacheInvalidate } from '@/lib/cache';
import type { Activity, CreateActivityInput, UpdateActivityInput } from '@/lib/types';

interface ActivitiesOptions {
  status?: 'unreflected';
}

interface ActivitiesState {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  retry: () => void;
  createActivity: (input: CreateActivityInput) => Promise<Activity>;
  updateActivity: (id: string, input: UpdateActivityInput) => Promise<Activity>;
  deleteActivity: (id: string) => Promise<void>;
}

export function useActivities(options: ActivitiesOptions = {}): ActivitiesState {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const { status } = options;

  const retry = useCallback((): void => {
    setFetchKey(k => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchActivities(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const params = status ? `?status=${status}` : '';
        const data = await api.get<Activity[]>(`/api/activities${params}`);
        if (!cancelled) setActivities(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load activities');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchActivities();
    return () => { cancelled = true; };
  }, [fetchKey, status]);

  const invalidateExportCache = useCallback((): void => {
    cacheInvalidate('export-fields');
  }, []);

  const createActivity = useCallback(async (input: CreateActivityInput): Promise<Activity> => {
    const activity = await api.post<Activity>('/api/activities', input);
    invalidateExportCache();
    setActivities(prev => [activity, ...prev]);
    return activity;
  }, [invalidateExportCache]);

  const updateActivity = useCallback(async (id: string, input: UpdateActivityInput): Promise<Activity> => {
    const updated = await api.put<Activity>(`/api/activities/${id}`, input);
    invalidateExportCache();
    setActivities(prev => prev.map(a => a.id === id ? updated : a));
    return updated;
  }, [invalidateExportCache]);

  const deleteActivity = useCallback(async (id: string): Promise<void> => {
    await api.delete(`/api/activities/${id}`);
    invalidateExportCache();
    setActivities(prev => prev.filter(a => a.id !== id));
  }, [invalidateExportCache]);

  return {
    activities,
    loading,
    error,
    retry,
    createActivity,
    updateActivity,
    deleteActivity,
  };
}
